// What the panel asks a booking link to hold.
//
// A link used to be minted for whichever service and provider the dropdown
// showed at that moment, whatever was in the offer — so the Wellness exam
// button's lab draw and doctor's visit were both held in one provider's
// diary, and the patient could book only one of them. Each time now goes
// with its own service and provider; one that names neither (an older saved
// offer) takes the dropdown's, as before.

interface PanelSlot {
  date?: string;
  time?: string;
  duration?: number;
  serviceId?: string;
  specialistId?: string;
}

interface Dropdown {
  service?: { id?: string } | null;
  specialist?: { id?: string } | null;
}

interface HeldRequestSlot {
  date: string;
  time: string;
  durationMinutes?: number;
  serviceId?: string;
  specialistId?: string;
}

const clock = (time?: string): string => String(time || '').slice(0, 5);

/** The body of POST /scheduling-links for this offer. */
export function linkRequest(offered: PanelSlot[], dropdown: Dropdown | null): {
  serviceId?: string; specialistId?: string; slots: HeldRequestSlot[];
} {
  const slots = offered.map((slot) => ({
    date: String(slot.date || ''),
    time: clock(slot.time),
    durationMinutes: slot.duration,
    serviceId: slot.serviceId || dropdown?.service?.id,
    specialistId: slot.specialistId || dropdown?.specialist?.id,
  }));
  // The link row itself is for its first appointment; an open link, which
  // holds nothing, is for whatever the dropdown shows.
  return {
    serviceId: slots[0]?.serviceId || dropdown?.service?.id,
    specialistId: slots[0]?.specialistId || dropdown?.specialist?.id,
    slots,
  };
}

/** The offer divided by the diary each time would be held in, for the hold preview. */
export function previewGroups(offered: PanelSlot[], dropdown: Dropdown | null): Array<{
  serviceId?: string; specialistId?: string; slots: Array<{ date: string; time: string; durationMinutes?: number }>;
}> {
  const groups: Array<{ serviceId?: string; specialistId?: string; slots: Array<{ date: string; time: string; durationMinutes?: number }> }> = [];
  linkRequest(offered, dropdown).slots.forEach((s) => {
    let group = groups.find((g) => g.serviceId === s.serviceId && g.specialistId === s.specialistId);
    if (!group) { group = { serviceId: s.serviceId, specialistId: s.specialistId, slots: [] }; groups.push(group); }
    group.slots.push({ date: s.date, time: s.time, durationMinutes: s.durationMinutes });
  });
  return groups;
}

/**
 * What "Create a booking link" will do with this offer, said before it is
 * pressed. Two appointments are one link the patient books together — not
 * "tap one", which is what the hint said when a link could carry only one.
 */
export function holdLinkHint(offered: PanelSlot[], dropdown: Dropdown | null, expires: string): string {
  const count = offered.length;
  if (!count) {
    return 'Choose the times you want to hold first. This link offers exactly those times and reserves them for this patient.';
  }
  const appointments = previewGroups(offered, dropdown).length;
  const tail = `nothing for you to confirm. Works once, expires ${expires}.`;
  if (appointments > 1) {
    const both = appointments === 2 ? 'both' : `all ${appointments}`;
    return `Creates one link for ${appointments} appointments and holds each time for this patient, with its own provider — nobody else is offered them while the link is live. The patient books ${both} together; ${tail}`;
  }
  const one = count === 1;
  return `Creates a link that offers exactly the ${one ? 'time' : `${count} times`} you have chosen and holds ${one ? 'it' : 'them'} for this patient — nobody else is offered ${one ? 'it' : 'them'} while the link is live. They tap ${one ? 'it' : 'one'} and it is booked; ${tail}`;
}
